<template>
  <ModalCardtable
    v-if="modalShowCardTable"
    @closeModal="closeModal"
    @toggleBlur="toggleBlur"
  />
  <ModalResetTable
    v-if="modalShowResetTable"
    @closeModal="closeResetTableModal"
    :id="selectedResetTableId"
  />
  <div class="bg-white dark:bg-dark-900 overflow-hidden rounded-lg shadow-lg">
    <!-- Header with Search and Filter -->
    <div class="p-6 border-b border-gray-100 dark:border-dark-800">
      <div
        class="flex flex-col md:flex-row items-start justify-between md:items-center gap-2"
      >
        <div>
          <h2 class="text-3xl font-bold text-gray-800 dark:text-gray-200">
            Daftar Meja
          </h2>
          <p class="text-base text-gray-500 dark:text-gray-400 mt-1">
            Pilih meja untuk mengelola pesanan
          </p>
        </div>
        <div class="flex gap-2 flex-col sm:flex-row">
          <div class="relative">
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Cari Meja..."
              class="pl-10 pr-4 py-2 border dark:border-dark-700 rounded-lg bg-gray-50 dark:bg-dark-800 text-gray-900 dark:text-gray-100"
            />
            <MagnifyingGlassIcon
              class="h-5 w-5 text-gray-400 absolute left-3 top-2.5"
            />
          </div>
          <select
            v-model="statusFilter"
            class="px-4 py-2 border dark:border-dark-700 rounded-lg bg-gray-50 dark:bg-dark-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">Semua Status</option>
            <option
              v-for="status in tableStatuses"
              :key="status"
              :value="status"
            >
              {{ formatStatus(status) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table Grid -->
    <div class="p-4">
      <div
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-5 xl:grid-cols-7 2xl:flex 2xl:flex-wrap gap-2 md:gap-4"
        v-if="showCards"
      >
        <div
          v-for="table in filteredTables"
          :key="table.id"
          class="relative group cursor-pointer"
          @click="handleTableClick(table)"
        >
          <div
            class="p-4 flex flex-col transition-all duration-200 shadow-md hover:shadow-lg relative"
            :class="[
              getTableStyles(table.status),
              table.id === selectedTableId &&
                allowedModal.includes(table.status) &&
                'ring-4 ring-blue-500 dark:ring-blue-400',
            ]"
          >
            <!-- Order Information (shown only when table is unavailable/occupied) -->
            <template
              v-if="table.status === 'unavailable' && getTableOrder(table.id)"
            >
              <div
                class="absolute top-1 left-1 right-1 flex justify-start items-center"
              >
                <span
                  class="text-[8px] md:text-xs font-medium text-gray-500 dark:text-gray-400"
                >
                  {{ getTableOrder(table.id).code }}
                </span>
              </div>
            </template>

            <template
              v-if="table.status === 'unavailable' && !getTableOrder(table.id)"
            >
              <div
                class="absolute top-1 left-1 right-1 flex justify-between items-center"
              >
                <span
                  class="text-xs font-medium text-gray-900 dark:text-gray-100"
                >
                  No Order
                </span>
              </div>
            </template>

            <div class="flex-1 flex flex-col items-center justify-center">
              <h3
                class="text-xl md:text-2xl font-bold"
                :class="[
                  getTableTextStyles(table.status),
                  table.id === selectedTableId &&
                    allowedModal.includes(table.status) &&
                    'text-blue-700 dark:text-blue-300',
                ]"
              >
                {{ table.name }}
              </h3>

              <div class="flex flex-col items-center">
                <span
                  class="flex items-center text-[8px] md:text-xs lg:text-xs font-light px-2 py-0.5 md:px-3 md:py-1 rounded-full"
                  :class="[
                    getStatusBadgeStyles(table.status),
                    table.id === selectedTableId &&
                      allowedModal.includes(table.status) &&
                      'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
                  ]"
                >
                  {{ formatStatus(table.status) }}
                  <div
                    v-if="
                      table.status == 'maintenance' || (table.status == 'unavailable' &&
                      getTableOrder(table.id) == undefined)
                    "
                  >
                    <ArrowPathIcon class="h-3 w-3 ml-1" />
                  </div>
                </span>

                <div
                  class="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                >
                  <span
                    class="flex items-center gap-1 text-xs md:text-sm lg:text-base"
                  >
                    <UserGroupIcon class="h-4 w-4" />
                    {{ table.capacity }}
                  </span>
                  <template
                    v-if="
                      table.status === 'unavailable' && getTableOrder(table.id)
                    "
                  >
                    <span class="text-xs md:text-sm truncate max-w-[100px]">
                      {{
                        getTableOrder(table.id).customer_name || "-"
                      }}
                    </span>
                  </template>
                </div>
              </div>
            </div>
            <!-- Order Information (shown only when table is unavailable/occupied) -->
            <template
              v-if="table.status === 'unavailable' && getTableOrder(table.id)"
            >
              <div
                class="absolute bottom-1 left-1 right-1 flex justify-end items-center"
              >
                <span
                  class="text-[8px] md:text-xs text-gray-500 dark:text-gray-500"
                >
                  {{ formatTime(getTableOrder(table.id).createdAt) }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ModalCardtable from "./modals/ModalCardtable.vue";
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";
import ModalResetTable from "./modals/ModalResetTable.vue";
import io from "socket.io-client";

export default {
  name: "CardTable",
  components: {
    ModalCardtable,
    MagnifyingGlassIcon,
    UserGroupIcon,
    ArrowPathIcon,
    ModalResetTable,
  },
  props: {
    tables: {
      type: Array,
      required: true,
    },
    allowedModal: {
      type: Array,
      default: () => ["available", "ready"],
    },
    selectedTableId: {
      type: Number,
      default: null,
    },
  },
  emits: ["table-selected"],
  data() {
    return {
      searchQuery: "",
      statusFilter: "",
      modalShowCardTable: false,
      modalShowResetTable: false,
      selectedResetTableId: null,
      isBlur: false,
      tableStatuses: [
        "available",
        "unavailable",
        "maintenance",
      ],

      refreshKey: 0,
      showCards: true,

      isConnected: false,
      isLoading: false,
      socket: null,
      message: "",
      messages: [],
    };
  },
  computed: {
    ...mapGetters("tables", ["allTables"]),
    ...mapGetters("cash_registers", ["allCashRegisters"]),
    ...mapGetters("sales_orders", ["allSalesOrders"]),
    relevantCashRegister() {
      return this.allCashRegisters?.find((cr) => cr.time_end == null);
    },
    filteredTables() {
      if (!this.showCards) return [];

      if (!this.allTables || !this.allSalesOrders || !this.relevantCashRegister)
        return [];

      return this.allTables
        .map((table) => {
          // Make a copy of the table
          const tableCopy = { ...table };

          // Check if table has an active order in current cash register
          const salesOrder = this.getRelevantSalesOrder(tableCopy.id);

          // Only mark as unavailable if we actually found a sales order
          if (salesOrder !== undefined) {
            tableCopy.status = "unavailable";
          }

          const matchesSearch = tableCopy.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase());
          const matchesStatus =
            !this.statusFilter || tableCopy.status === this.statusFilter;

          // Only include tables that match our filters
          return matchesSearch && matchesStatus ? tableCopy : null;
        })
        .filter(Boolean); // Remove null entries
    },
  },
  watch: {
    allTables: {
      immediate: true,
      handler() {
        // console.log("Watcher triggered: allTables updated:", newValue);
      },
    },
    allSalesOrders: {
      immediate: true,
      handler() {
        // console.log("Watcher triggered: allSalesOrders updated:", newValue);
      },
    },
    relevantCashRegister: {
      immediate: true,
      handler(newValue) {
        // console.log("Watcher triggered: relevantCashRegister updated:", newValue);

        if (!newValue) {
          // console.warn("relevantCashRegister is null or undefined.");
          return;
        }

        this.$store.dispatch("sales_orders/fetchSalesOrders", {
          limit: 100,
          cash_register_id: newValue.id,
        });
      },
    },
  },
  created() {
    this.$store.dispatch("tables/fetchTables", { limit: 100 });
    this.$store.dispatch("cash_registers/fetchCashRegisters", {status: "open"});
    // if (this.relevantCashRegister) {
    //   this.$store.dispatch("sales_orders/fetchSalesOrders", {
    //     cash_register_id: this.relevantCashRegister.id,
    //   });
    // }

    this.connectWebSocket();
  },
  methods: {
    handleTableClick(table) {
      if (table.status === "unavailable" || table.status === "maintenance") {
        const salesOrder = this.getRelevantSalesOrder(table.id);
        if (salesOrder) {
          this.$router.push({
            name: "order",
            query: { orderId: salesOrder.id },
          });
        } else {
          this.openResetTableModal(table.id);
        }
      } else if (table.status === "available") {
        this.$router.push({
          name: "newTransaction",
          query: {
            tableId: table.id,
            orderType: "dine_in",
          },
        });
      }
    },
    formatStatus(status) {
      return status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    getTableStyles(status) {
      const styles = {
        available:
          "bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500",
        ready:
          "bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500",
        reserved: "bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500",
        not_ordered: "bg-gray-50 dark:bg-gray-800 border-2 border-gray-500",
        maintenance:
          "bg-orange-50 dark:bg-orange-900/30 border-2 border-orange-500",
        unavailable: "bg-red-50 dark:bg-red-900/30 border-2 border-red-500",
      };
      return styles[status] || styles["unavailable"];
    },
    getTableTextStyles(status) {
      const styles = {
        available: "text-emerald-700 dark:text-emerald-300",
        ready: "text-emerald-700 dark:text-emerald-300",
        reserved: "text-blue-700 dark:text-blue-300",
        not_ordered: "text-gray-700 dark:text-gray-300",
        maintenance: "text-orange-700 dark:text-orange-300",
        unavailable: "text-red-700 dark:text-red-300",
      };
      return styles[status] || styles["unavailable"];
    },
    getStatusBadgeStyles(status) {
      const styles = {
        available:
          "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300",
        ready:
          "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300",
        reserved:
          "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300",
        not_ordered:
          "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300",
        maintenance:
          "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300",
        unavailable:
          "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300",
      };
      return styles[status] || styles["unavailable"];
    },
    openModal() {
      this.modalShowCardTable = true;
      this.isBlur = true;
    },
    closeModal() {
      this.modalShowCardTable = false;
      this.isBlur = false;
    },
    openResetTableModal(tableId) {
      this.modalShowResetTable = true;
      this.isBlur = true;
      this.selectedResetTableId = tableId;
      this.showCards = false;
    },
    closeResetTableModal() {
      this.modalShowResetTable = false;
      this.isBlur = false;
      this.selectedResetTableId = null;
      this.$store.dispatch("tables/fetchTables", { limit: 100 }).then(() => {
        this.refreshKey++;
        this.showCards = true;
      });
    },
    getRelevantSalesOrder(tableId) {
      if (!this.relevantCashRegister) return undefined;

      // console.log('Checking table:', tableId);
      // console.log('Current cash register:', this.relevantCashRegister.id);
      // console.log('All sales orders:', this.allSalesOrders);
      const allowedResetTable = ["paid", "canceled"];

      const salesOrder = this.allSalesOrders?.find((so) => {
        // console.log('Checking sales order:', so.id, 'table_id:', so.table_id, 'status:', so.status, 'cash_register:', so.cash_register_id);
        return (
          so.table_id == tableId &&
          !allowedResetTable.includes(so.status) &&
          so.cash_register_id == this.relevantCashRegister.id
        );
      });

      // console.log('Found sales order:', salesOrder);
      return salesOrder;
    },
    getTableOrder(tableId) {
      return this.getRelevantSalesOrder(tableId);
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    connectWebSocket() {
      try {
        this.socket = io(process.env.VUE_APP_WEBSOCKET_URL, {
          reconnection: true, // Enable auto-reconnection
          reconnectionAttempts: 5, // Maximum number of reconnection attempts
          timeout: 10000, // Connection timeout in milliseconds
        });

        this.socket.on("connect_error", (error) => {
          this.messages.push(error.message);
          this.isConnected = false;
        });

        this.socket.on("disconnect", (reason) => {
          this.messages.push(reason);
          this.isConnected = false;
        });

        // Menerima pesan dari server
        this.socket.on("serverMessage", (data) => {
          this.handleWebSocketMessage(data);
          this.messages.push(data.message);
        });

        this.socket.on("welcome", (data) => {
          console.log("WELCOME: ", data);
          this.messages.push(data.message);
        });

        this.socket.on("new_items", (table) => {
          console.log("NEW_ITEMS: ", table);
          this.handleWebSocketMessage(table);
        });

        this.socket.on("update_items", (table) => {
          this.handleWebSocketMessage(table);
        });

        this.socket.on("delete_items", (table) => {
          this.handleWebSocketMessage(table);
        });

        this.socket.on("connect", () => {
          this.isConnected = true;
        });

        this.socket.on("error", (error) => {
          this.messages.push(error.message);
          this.isConnected = false;
        });

        this.handleReconnect();
      } catch (error) {
        this.isConnected = false;
      }
    },
    sendMessage() {
      // Kirim pesan ke server
      this.socket.emit("clientMessage", this.message);
      this.message = ""; // Reset input
    },
    async fetchSalesOrders() {
      await this.$store.dispatch("sales_orders/fetchSalesOrders", {
        limit: 100,
        cash_register_id: this.relevantCashRegister?.id,
      });
    },
    async handleWebSocketMessage(data) {
      console.log("handleWebSocketMessage: ", data);
      this.isLoading = true;
      try {
        // Validasi data terlebih dahulu
        if (!data || !data.type) {
          return;
        }

        switch (data.type) {
          case "NEW_ITEMS":
            if (data.items && data.route == "table") {
              this.$store.commit("tables/addTable", data.items);
              await this.fetchSalesOrders();
            }
            break;
          case "UPDATE_ITEMS":
            if (data.items && data.items.id && data.route == "table") {
              this.$store.commit("tables/updateTable", data.items);
              await this.fetchSalesOrders();
            }
            break;
          case "DELETE_ITEMS":
            if (data.id && data.route == "table") {
              this.listTables = this.listTables.filter(
                (table) => String(table.id) !== String(data.id)
              );
              await this.fetchSalesOrders();
            }
            break;
          default:
            console.warn("Unknown message type:", data.type);
        }
      } catch (error) {
        console.error("Error handling websocket message:", error);
      } finally {
        this.isLoading = false;
      }
    },
    handleReconnect() {
      if (this.socket) {
        this.socket.on("reconnect_attempt", (attemptNumber) => {
          console.log(`Reconnection attempt ${attemptNumber}`);
        });

        this.socket.on("reconnect", () => {
          console.log("Reconnected successfully");
          this.isConnected = true;
          // Mungkin perlu memuat ulang data
          this.socket.emit("requestInitialData");
        });

        this.socket.on("reconnect_failed", () => {
          console.log("Failed to reconnect");
          this.isConnected = false;
        });
      }
    },
  },
};
</script>
